import { Link } from "react-router-dom";
import { Clock, Crown } from "lucide-react";
import { Article } from "@/lib/types";
import { timeAgo } from "@/lib/api";
import { useCardTx, useLang, useArticlePath } from "@/lib/i18n";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1621761191319-c6fb62004040?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1563986768609-322da13575f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];
const fallbackFor = (s: string) =>
  FALLBACKS[Array.from(s || "x").reduce((a, c) => a + c.charCodeAt(0), 0) % FALLBACKS.length];

export const CategoryChip = ({ slug, asLink = false }: { slug: string; asLink?: boolean }) => {
  const cls = "inline-block font-mono text-[10px] uppercase tracking-widest text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-2 py-0.5 rounded hover:bg-[#00E5FF]/20 transition-colors";
  const label = slug.replace("-", " / ");
  if (asLink) return <Link to={`/${slug}`} className={cls}>{label}</Link>;
  return <span className={cls}>{label}</span>;
};

export const KickerBox = ({ label }: { label?: string | null }) =>
  label ? (
    <span data-testid="kicker-box"
      className="absolute bottom-3 left-3 z-10 bg-[#7C3AED]/[0.88] px-3 py-1.5 rounded-md font-news font-bold text-white text-[17px] leading-none tracking-wide uppercase shadow-lg">
      {label}
    </span>
  ) : null;

export const ReadTime = ({ minutes, className = "" }: { minutes?: number | null; className?: string }) => {
  const { t } = useLang();
  return minutes ? (
    <span data-testid="read-time"
      className={`inline-flex items-center gap-1 font-mono text-[10px] text-gray-500 whitespace-nowrap ${className}`}>
      <Clock className="h-3 w-3" /> {minutes} {t("minRead")}
    </span>
  ) : null;
};

export const PremiumChip = () => (
  <span data-testid="premium-badge"
    className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-[#7C3AED] text-white font-heading font-bold text-[9px] tracking-[0.15em] uppercase px-2 py-1 rounded">
    <Crown className="h-3 w-3" /> Premium
  </span>
);

export const ArticleCard = ({ article, variant = "default" }: { article: Article; variant?: "default" | "compact" | "featured" }) => {
  const { t } = useLang();
  const tx = useCardTx(article.slug);
  const toArticle = useArticlePath();
  const title = tx?.title || article.title;
  const summary = tx?.summary || article.summary_ai || article.summary;
  const kicker = tx?.kicker || article.kicker;
  if (variant === "compact") {
    return (
      <Link to={toArticle(article.slug)} data-testid={`article-card-${article.slug}`}
        className="group flex gap-4 items-start py-3 border-b border-white/5 last:border-0">
        <img src={article.hero_image_url || fallbackFor(article.slug)} alt={title} loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).src = fallbackFor(article.slug); }}
          className="w-20 h-14 object-cover rounded shrink-0 opacity-90 group-hover:opacity-100 transition-opacity" />
        <div className="min-w-0">
          <h4 className="font-news text-sm font-semibold text-gray-200 group-hover:text-[#00E5FF] transition-colors line-clamp-2 leading-snug">
            {article.access_level === "premium" && <Crown className="h-3 w-3 text-[#A78BFA] inline mr-1 -mt-0.5" />}
            {title}
          </h4>
          <p className="font-mono text-[10px] text-gray-500 mt-1.5 flex items-center gap-2 flex-wrap">
            <span>{timeAgo(article.published_at)}</span>
            <ReadTime minutes={article.read_minutes} />
          </p>
        </div>
      </Link>
    );
  }
  if (variant === "featured") {
    // Single-inventory editorial card: horizontal on desktop, stacked on mobile.
    return (
      <article data-testid={`article-card-${article.slug}`}
        className={`group h-full bg-[#121212] rounded-lg overflow-hidden transition-all duration-200 hover:-translate-y-1 border ${
          article.is_breaking ? "border-transparent" : "border-[#7C3AED]/60 hover:border-[#7C3AED]"}`}>
        <Link to={toArticle(article.slug)} className="flex flex-col sm:flex-row h-full">
          <div className="relative aspect-[16/9] sm:aspect-auto sm:w-[46%] sm:min-h-[230px] shrink-0 overflow-hidden bg-[#1A1A1A]">
            <img src={article.hero_image_url || fallbackFor(article.slug)} alt={title} loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).src = fallbackFor(article.slug); }}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            {article.is_breaking && (
              <div data-testid="breaking-strip"
                className="absolute top-0 inset-x-0 z-10 bg-[#DC2626]/[0.92] text-white text-center font-heading font-bold text-[10px] tracking-[0.25em] uppercase py-1.5">
                {t("breaking")}
              </div>
            )}
            <KickerBox label={kicker} />
            {article.access_level === "premium" && <PremiumChip />}
          </div>
          <div className="p-5 sm:p-6 flex flex-col flex-1 min-w-0">
            <h3 className="font-news font-bold text-white text-xl sm:text-2xl leading-snug line-clamp-3 group-hover:text-[#00E5FF] transition-colors">
              {title}
            </h3>
            {summary && (
              <p className="text-[15px] text-gray-400 mt-3 line-clamp-3 leading-relaxed">
                {summary}
              </p>
            )}
            <div className="flex items-center justify-between gap-2 mt-auto pt-4">
              <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate">
                {timeAgo(article.published_at)}
              </p>
              <ReadTime minutes={article.read_minutes} className="shrink-0" />
            </div>
          </div>
        </Link>
      </article>
    );
  }
  return (
    <article data-testid={`article-card-${article.slug}`}
      className={`group h-full bg-[#121212] rounded-lg overflow-hidden transition-all duration-200 hover:-translate-y-1 border ${
        article.is_breaking ? "border-transparent" : "border-[#7C3AED]/60 hover:border-[#7C3AED]"}`}>
      <Link to={toArticle(article.slug)} className="flex flex-col h-full">
        <div className="relative aspect-[16/9] overflow-hidden bg-[#1A1A1A]">
          <img src={article.hero_image_url || fallbackFor(article.slug)} alt={title} loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).src = fallbackFor(article.slug); }}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          {article.is_breaking && (
            <div data-testid="breaking-strip"
              className="absolute top-0 inset-x-0 z-10 bg-[#DC2626]/[0.92] text-white text-center font-heading font-bold text-[10px] tracking-[0.25em] uppercase py-1.5">
              {t("breaking")}
            </div>
          )}
          <KickerBox label={kicker} />
          {article.access_level === "premium" && <PremiumChip />}
        </div>
        <div className="p-4 sm:p-5 flex flex-col flex-1">
          <h3 className="font-news font-bold text-white text-base leading-snug line-clamp-3 group-hover:text-[#00E5FF] transition-colors">
            {title}
          </h3>
          <p className="text-sm text-gray-400 mt-2 line-clamp-2 leading-relaxed">
            {summary}
          </p>
          <div className="flex items-center justify-between gap-2 mt-auto pt-3">
            <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate">
              {timeAgo(article.published_at)}
            </p>
            <ReadTime minutes={article.read_minutes} className="shrink-0" />
          </div>
        </div>
      </Link>
    </article>
  );
};
